import React, { useEffect, useMemo, useState } from "react";
import { Button, Empty, Input as AntInput, Popconfirm, Popover, Skeleton } from "antd";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { ReactComponent as StarIcon } from "../../../assets/icons/star-icon.svg";
import "./AbandonedCart.css";
import {
  useLazyGetAbandonedCartDetailQuery,
  useLazyGetReportingManagersQuery,
  useUpdateAbandonedCartStatusMutation,
} from "../../../apis/abandonedcartAPI";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../NotificationToast/NotificationToast";
import {
  dateFormat,  
} from "../../../commonFuntions/CommonUtilFunctions";

const formatMoney = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;
const statusLabelMap = { abandoned: "Abandoned", recovered: "Recovered", lost: "Lost" };
const chipClasses = ["chip-blue", "chip-gray", "chip-amber", "chip-teal", "chip-purple"];
const customerTypeOptions = [
  { value: "Homeowner", label: "Homeowner", initials: "HO", chip: "chip-blue", searchText: "homeowner" },
  { value: "Architects", label: "Architects", initials: "AR", chip: "chip-gray", searchText: "architects" },
  { value: "Builders", label: "Builders", initials: "BU", chip: "chip-amber", searchText: "builders" },
  { value: "Interior designers", label: "Interior designers", initials: "ID", chip: "chip-teal", searchText: "interior designers" },
  { value: "Developers", label: "Developers", initials: "DE", chip: "chip-purple", searchText: "developers" },
  { value: "Contractors", label: "Contractors", initials: "CO", chip: "chip-blue", searchText: "contractors" },
  { value: "Vendors", label: "Vendors", initials: "VE", chip: "chip-gray", searchText: "vendors" },
  { value: "Others", label: "Others", initials: "OT", chip: "chip-amber", searchText: "others" },
];
const toInitials = (name = "") =>
  String(name)
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() || "")
    .join("") || "NA";

const AbandonedCartDetails = () => {
  const [getAbandonedCartDetailApi, { data: abandonedCartDetailData, isFetching: isDetailFetching }] =
    useLazyGetAbandonedCartDetailQuery();
  const [getReportingManagersApi, { data: reportingManagersData }] =
    useLazyGetReportingManagersQuery();
  const [updateAbandonedCartStatusApi, { isLoading: updateStatusLoading }] =
    useUpdateAbandonedCartStatusMutation();
  const [activityCollapsed, setActivityCollapsed] = useState(false);
  const [assignedTo, setAssignedTo] = useState(undefined);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSearch, setAssignSearch] = useState("");
  const [customerType, setCustomerType] = useState(undefined);
  const [customerTypeOpen, setCustomerTypeOpen] = useState(false);
  const [customerTypeSearch, setCustomerTypeSearch] = useState("");
  const [noteText, setNoteText] = useState("");
  const navigate = useNavigate();
  const { cartId } = useParams();

  useEffect(() => {
    if (cartId) getAbandonedCartDetailApi(cartId);
    getReportingManagersApi();
  }, [cartId, getAbandonedCartDetailApi, getReportingManagersApi]);

  const assigneeOptions = useMemo(() => {
    const rows =
      reportingManagersData?.data?.results ||
      reportingManagersData?.data ||
      reportingManagersData?.results ||
      [];
    return (Array.isArray(rows) ? rows : []).map((user, idx) => {
      const name =
        user?.reporting_person ||
        user?.full_name ||
        user?.name ||
        user?.username ||
        user?.email ||
        `User ${idx + 1}`;
      return {
        id: user?.id,
        value: name,
        label: name,
        initials: toInitials(name),
        chip: chipClasses[idx % chipClasses.length],
        searchText: String(name).toLowerCase(),
      };
    });
  }, [reportingManagersData]);

  const cart = useMemo(() => {
    const row =
      abandonedCartDetailData?.data ||
      abandonedCartDetailData?.result ||
      abandonedCartDetailData ||
      null;
    if (!row) return null;

    const regularSubcarts = Array.isArray(row?.cart?.subcarts)
      ? row.cart.subcarts
      : [];
    const quickSubcarts = Array.isArray(row?.cart?.quick_commerce?.subcarts)
      ? row.cart.quick_commerce.subcarts
      : [];
    const cartSubcarts = regularSubcarts.length > 0 ? regularSubcarts : quickSubcarts;
    const firstSubcart = cartSubcarts.length
      ? cartSubcarts[0]
      : null;
    const allRawProducts = cartSubcarts.length > 0
      ? cartSubcarts.flatMap((sc) => sc?.items || [])
      : (row?.cart?.quote_cart?.items || row?.products || row?.items || []);
    const normalizedProducts = allRawProducts;
    const customer = row?.customer || {};
    const customerDetails = row?.customer_details || {};
    const summary = row?.cart || {};
    const deliveryAddressObj = row?.delivery_address || row?.billing_address || row?.address;
    const deliveryAddressText =
      typeof deliveryAddressObj === "string"
        ? deliveryAddressObj
        : deliveryAddressObj && typeof deliveryAddressObj === "object"
        ? [
            deliveryAddressObj?.address_line_1,
            deliveryAddressObj?.address_line_2,
            deliveryAddressObj?.city,
            deliveryAddressObj?.state,
            deliveryAddressObj?.pincode,
          ]
            .filter(Boolean)
            .join(", ")
        : "-";

    return {
      ...row,
      id: row?.cart_number || row?.cart_id || row?.id || cartId,
      status: (row?.status || "abandoned").toLowerCase(),
      abandoned_text: row?.abandoned_text || row?.updated_at_text || row?.last_item_activity_at || row?.created_at || "-",
      customer_name:
        customer?.name ||
        customerDetails?.full_name ||
        row?.customer_name ||
        row?.name ||
        "-",
      customer_mobile:
        customer?.phone_number ||
        customerDetails?.phone_number ||
        row?.customer_mobile ||
        row?.phone_number ||
        row?.mobile ||
        "-",
      delivery_address: deliveryAddressText || "-",
      items_count:
        Number(
          row?.items_count?.sku_count ??
            row?.cart?.item_count ??
            row?.total_items ??
            normalizedProducts.length ??
            0
        ) || 0,
      cart_total: Number(row?.cart_value ?? summary?.total ?? row?.cart_total ?? row?.total ?? 0) || 0,
      previous_order_count: Number(row?.previous_order_count ?? 0) || 0,
      gst_number: customerDetails?.gst_number || "-",
      customer_type:
        row?.customer_type ||
        customer?.customer_type ||
        customerDetails?.customer_type ||
        undefined,
      earning_points: Number(summary?.earning_points ?? 0) || 0,
      order_subtotal: Number(summary?.sub_cart_total ?? 0) || 0,
      order_shipping: Number(summary?.shipping ?? 0) || 0,
      delivery_shipping:
        Number(
          firstSubcart?.shipping ??
            row?.cart?.quick_commerce?.subcarts?.[0]?.shipping ??
            summary?.shipping ??
            0
        ) || 0,
      order_tax: Number(summary?.tax ?? 0) || 0,
      order_savings: Number(summary?.savings ?? 0) || 0,
      vendor_name: firstSubcart?.vendor?.vendor_name || "BENG-098",
      vendor_bmp_id: firstSubcart?.vendor?.bmp_id || "BENG-098",
      delivery_time: firstSubcart?.fullfillment_latency || row?.cart?.quick_commerce?.quick_delivery || "1-4 hrs",
      activity_logs: Array.isArray(row?.activity_logs) ? row.activity_logs : [],
      notes: Array.isArray(row?.notes) ? row.notes : [],
      subcarts: cartSubcarts.length > 0
        ? cartSubcarts.map((sc) => ({
            vendor_name: sc?.vendor?.vendor_name || "-",
            vendor_bmp_id: sc?.vendor?.bmp_id || "-",
            delivery_time: sc?.fullfillment_latency || "-",
            shipping: Number(sc?.shipping ?? 0),
            products: (sc?.items || []).map((p) => ({
              ...p,
              name: p?.product?.product_name || p?.name || p?.product_name || "-",
              product_image: p?.product?.product_image || p?.product_image || "",
              unit_price: Number(p?.product?.vendor_selling_price ?? p?.unit_price ?? p?.price ?? 0) || 0,
              qty: p?.quantity ?? p?.qty ?? 0,
              sku: p?.product?.mob_sku || p?.sku || p?.product_sku || "-",
            })),
          }))
        : null,
      products: normalizedProducts.map((p) => ({
        ...p,
        name: p?.product?.product_name || p?.name || p?.product_name || "-",
        product_image: p?.product?.product_image || p?.product_image || "",
        unit_price: Number(p?.product?.vendor_selling_price ?? p?.unit_price ?? p?.price ?? 0) || 0,
        qty: p?.quantity ?? p?.qty ?? 0,
        sku: p?.product?.mob_sku || p?.sku || p?.product_sku || "-",
      })),
    };
  }, [abandonedCartDetailData, cartId]);

  const orderSummary = useMemo(() => {
    const subtotal = Number(cart?.order_subtotal ?? cart?.cart_total ?? 0);
    const shipping = Number(cart?.order_shipping ?? 0);
    const tax = Number(cart?.order_tax ?? 0);
    const savings = Number(cart?.order_savings ?? 0);
    const total = Number(cart?.cart_total ?? subtotal + shipping + tax - savings);
    const points = Number(cart?.earning_points ?? Math.round(total / 100));
    return { subtotal, shipping, tax, savings, total, points };
  }, [cart]);
  const isClosedStatus = ["lost", "recovered"].includes(
    String(cart?.status || "").toLowerCase()
  );

  useEffect(() => {
    if (!cart?.assigned_to) {
      setAssignedTo(undefined);
      return;
    }
    if (typeof cart.assigned_to === "number") {
      const match = assigneeOptions.find((a) => a.id === cart.assigned_to);
      if (match) setAssignedTo(match.value);
      return;
    }
    if (typeof cart.assigned_to === "string") {
      setAssignedTo(cart.assigned_to);
      return;
    }
    if (typeof cart.assigned_to === "object") {
      setAssignedTo(
        cart.assigned_to?.reporting_person ||
        cart.assigned_to?.name ||
          cart.assigned_to?.full_name ||
          cart.assigned_to?.label ||
          undefined
      );
    }
  }, [cart, assigneeOptions]);

  useEffect(() => {
    setCustomerType(cart?.customer_type || undefined);
  }, [cart?.customer_type]);

  const activityItems = useMemo(() => {
    const logs = Array.isArray(cart?.activity_logs) ? cart.activity_logs : [];
    const logItems = logs.map((log, idx) => {
      const action = (log?.action || log?.type || "").toLowerCase();
      const messageText = String(log?.message || "").toLowerCase();
      let iconType = "note";
      if (action.includes("status_updated") || action.includes("status")) {
        if (messageText.includes("recovered")) iconType = "status-recovered";
        else if (messageText.includes("lost")) iconType = "status-lost";
        else iconType = "status-abandoned";
      } else if (action.includes("whatsapp") || action.includes("message")) iconType = "chat";
      else if (action.includes("auto") || action.includes("system")) iconType = "gear";
      else if (action.includes("assign") || action.includes("claim")) iconType = "user";
      else if (action.includes("abandon") || action.includes("checkout")) iconType = "cart";
      return {
        key: log?.id || `log-${idx}`,
        iconType,
        title: log?.title || log?.event || log?.action || "Activity updated",
        message: log?.message || "",
        meta: log?.created_at_text || log?.created_at || "",
        note: log?.comments || log?.note || "",
        createdAt: log?.created_at || "",
      };
    });
    const notes = Array.isArray(cart?.notes) ? cart.notes : [];
    const noteItems = notes.map((item, idx) => ({
      key: item?.id || `note-${idx}`,
      iconType: "note",
      title: "Note added",
      message: item?.created_by_name ? `By ${item.created_by_name}` : "",
      meta: item?.created_at || "",
      note: item?.note || "",
      createdAt: item?.created_at || "",
    }));

    return [...logItems, ...noteItems].sort((a, b) => {
      const aTime = new Date(a?.createdAt || 0).getTime();
      const bTime = new Date(b?.createdAt || 0).getTime();
      return bTime - aTime;
    });
  }, [cart?.activity_logs, cart?.notes]);

  const renderTimelineIcon = (type) => {
    if (type === "note") {
      return (
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
          <polyline points="14 2 14 8 20 8"></polyline>
          <line x1="16" y1="13" x2="8" y2="13"></line>
          <line x1="16" y1="17" x2="8" y2="17"></line>
        </svg>
      );
    }
    if (type === "status-recovered") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5"></path>
        </svg>
      );
    }
    if (type === "status-lost") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m18 6-12 12M6 6l12 12"></path>
        </svg>
      );
    }
    if (type === "status-abandoned") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="9"></circle>
          <path d="M12 8v5"></path>
          <path d="M12 16h.01"></path>
        </svg>
      );
    }
    if (type === "chat") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a4 4 0 0 1-4 4H8l-5 3V7a4 4 0 0 1 4-4h10a4 4 0 0 1 4 4z"></path>
        </svg>
      );
    }
    if (type === "gear") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06A2 2 0 1 1 4.4 17.4l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82L4.2 7.64A2 2 0 1 1 7.03 4.8l.06.06a1.65 1.65 0 0 0 1.82.33h.01A1.65 1.65 0 0 0 9.93 3.7V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h.01a1.65 1.65 0 0 0 1.82-.33l.06-.06A2 2 0 1 1 19.6 7l-.06.06a1.65 1.65 0 0 0-.33 1.82v.01a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
      );
    }
    if (type === "user") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
          <circle cx="8.5" cy="7" r="4"></circle>
          <path d="M20 8v6"></path>
          <path d="M23 11h-6"></path>
        </svg>
      );
    }
    if (type === "cart") {
      return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="9" cy="21" r="1"></circle>
          <circle cx="20" cy="21" r="1"></circle>
          <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
        </svg>
      );
    }
    return (
      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <circle cx="12" cy="12" r="5"></circle>
      </svg>
    );
  };

  const patchCart = async (body) => {
    if (!cart?.id) return false;
    const response = await updateAbandonedCartStatusApi({ id: cart.id, body });
    if (response?.error) {
      showErrorToast(
        response?.error?.data?.message ||
          response?.error?.data?.data?.status?.[0] ||
          "Failed to update cart"
      );
      return false;
    }
    showSuccessToast(response?.data?.message || "Updated successfully");
    getAbandonedCartDetailApi(cart.id);
    return true;
  };

  const handleAssignUser = async (opt) => {
    const ok = await patchCart({ assigned_to: opt.id });
    if (!ok) return;
    setAssignedTo(opt.value);
    setAssignOpen(false);
    setAssignSearch("");
  };

  const handleCustomerTypeChange = async (value) => {
    const ok = await patchCart({ customer_type: value });
    if (!ok) return;
    setCustomerType(value);
    setCustomerTypeOpen(false);
    setCustomerTypeSearch("");
  };

  const handleStatusUpdate = async (status) => {
    const payload = { status };
    if (status === "lost") {
      payload.reason = noteText?.trim() || "Not converted";
      payload.comments = noteText?.trim() || "Marked as lost";
    }
    await patchCart(payload);
  };

  const handleSaveNote = async () => {
    if (!noteText.trim()) {
      showErrorToast("Please enter note");
      return;
    }
    const ok = await patchCart({ comments: noteText.trim() });
    if (ok) setNoteText("");
  };
  const canSaveNote = Boolean(noteText.trim()) && !updateStatusLoading;

  const handleCopyField = async (value, label) => {
    const text = String(value || "").trim();
    if (!text || text === "-") {
      showErrorToast(`${label} not available`);
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      showSuccessToast(`${label} copied`);
    } catch (e) {
      showErrorToast(`Failed to copy ${label.toLowerCase()}`);
    }
  };

  if (isDetailFetching && !cart) {
    return (
      <div className="abandoned-page">
        <button className="back-btn" onClick={() => navigate("/admin/customer/abandoned-carts")}>← BACK</button>
        <div className="ac-details-skeleton-head">
          <Skeleton active paragraph={{ rows: 1, width: ["40%"] }} title={{ width: "30%" }} />
        </div>
        <div className="layout">
          <div className="cart-card ac-details-skeleton-card">
            <Skeleton active paragraph={{ rows: 10 }} />
          </div>
          <div className="side-stack">
            <div className="side-card"><Skeleton active paragraph={{ rows: 5 }} /></div>
            <div className="side-card"><Skeleton active paragraph={{ rows: 3 }} /></div>
            <div className="side-card"><Skeleton active paragraph={{ rows: 8 }} /></div>
          </div>
        </div>
      </div>
    );
  }

  if (!cart) {
    return (
      <div className="abandoned-page">
        <Button icon={<ArrowLeftOutlined />} className="abandoned-back-btn" onClick={() => navigate("/admin/customer/abandoned-carts")}>BACK</Button>
        <div className="table-card"><Empty description="Cart not found" /></div>
      </div>
    );
  }

  return (
    <div className="abandoned-page">
      <button className="back-btn" onClick={() => navigate("/admin/customer/abandoned-carts")}>← BACK</button>

      <div className="page-title-row">
        <div className="page-title-block">
          <div className="page-title-main">
            <h1>
              {cart.cart_total >= 20000 && (
                <span className="hot-banner-flame">🔥</span>
              )}
              Cart #{cart.id}
            </h1>
            <span className={`status-tag s-${cart.status}`}><span className="dot" />{statusLabelMap[cart.status]}</span>
          </div>
          <div className="page-title-sub">Abandoned <strong>{dateFormat(cart.abandoned_text)}</strong></div>
        </div>
      </div>

      <div className="layout">
        <div>
          <div className="cart-card">
            <div className="cart-card-header"><h2>Cart ({cart.items_count})</h2></div>
            <div className="address-section">
              <div className="address-row">
                <div className="address-inner">
                  <span className="address-icon" aria-hidden="true">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"></path>
                      <circle cx="12" cy="10" r="3"></circle>
                    </svg>
                  </span>
                  <div className="address-content">
                    <div className="address-label">Delivery address</div>
                    <div className="address-name">{cart.customer_name}</div>
                    <div className="address-text">{cart.delivery_address}</div>
                    <div className="address-phone">{cart.customer_mobile}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="seller-block">
              {(cart.subcarts || [{ vendor_name: cart.vendor_name, vendor_bmp_id: cart.vendor_bmp_id, delivery_time: cart.delivery_time, shipping: cart.delivery_shipping, products: cart.products }]).map((subcart, scIdx) => (
                <div className="seller-card" key={subcart.vendor_bmp_id || scIdx} style={scIdx > 0 ? { marginTop: "20px" } : undefined}>
                  <div className="seller-strip">
                    <span className="seller-strip-title">Store delivery</span>
                    <span className="seller-strip-price">
                      {Number(subcart.shipping || 0) === 0 ? (
                        "Free delivery"
                      ) : (
                        <>
                          <span className="old">₹400</span>{" "}
                          {formatMoney(subcart.shipping)}
                        </>
                      )}
                    </span>
                  </div>
                  <div className="seller-meta">
                    <span className="sold-by">Sold by {subcart.vendor_bmp_id || subcart.vendor_name || "-"}</span>
                    <span className="delivery-time">
                      {String(subcart.delivery_time || "").includes("1-4") ? (
                        <span className="qwik-badge"><span className="qwik-tag">QWIK</span></span>
                      ) : null}{" "}
                      {subcart.delivery_time || "-"}
                    </span>
                  </div>
                  <div className="line-items">
                    {subcart.products.map((product, index) => {
                      const qtyNumber = Number(String(product?.qty).replace(/[^\d.]/g, "")) || 0;
                      const lineTotal = qtyNumber * Number(product?.unit_price || 0);
                      return (
                        <div className="line-item" key={`${product.name}-${scIdx}-${index}`}>
                          <div className="line-num">{index + 1}</div>
                          <div className="line-thumb">
                            {product?.product_image ? (
                              <img src={product.product_image} alt={product.name} className="line-thumb-img" />
                            ) : (
                              "□"
                            )}
                          </div>
                          <div className="line-info">
                            <div className="line-name" title={product.name}>{product.name}</div>
                            <div className="line-unit-price">{formatMoney(product.unit_price)} / unit</div>
                            <div className="line-sku">SKU: {product.sku || "-"}</div>
                          </div>
                          <div className="line-qty"><span>Qty</span><strong>{product.qty}</strong></div>
                          <div className="line-total">{formatMoney(lineTotal)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}

              <div className="order-summary">
                <h3>Order Details</h3>
                <div className="summary-row"><span className="">Subtotal</span><span className="value">{formatMoney(orderSummary.subtotal)}</span></div>
                <div className="summary-row"><span className="">Shipping</span><span className="value">{formatMoney(orderSummary.shipping)}</span></div>
                <div className="summary-row"><span className=" dotted">Total tax (GST)</span><span className="value">{formatMoney(orderSummary.tax)}</span></div>
                <div className="summary-row"><span className=" dotted">Savings</span><span className="value">-{formatMoney(orderSummary.savings)}</span></div>
                <div className="summary-divider" />
                <div className="summary-total"><span className="">Total cart value</span><span className="value">{formatMoney(orderSummary.total)}</span></div>
                <div className="points-strip">
                  Customer would have earned <StarIcon className="points-star-icon" />
                  <strong>{orderSummary.points} points</strong> on this purchase
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="side-stack">
          <div className="side-card">
            <div className="side-card-title">Customer</div>
            <div className="customer-name">{cart.customer_name}</div>
            {/* <div className="customer-badges">
              <span className="cust-badge b2b">B2B</span>
              <span className="cust-badge returning">Returning</span>
            </div> */}
            <div className="contact-list">
              <div className="contact-row">
                <span className="label-text">Phone</span>
                <span className="value-text">
                  {cart.customer_mobile}
                  <button
                    className="copy-btn"
                    type="button"
                    aria-label="Copy phone"
                    onClick={() => handleCopyField(cart.customer_mobile, "Phone")}
                  >
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </span>
              </div>
              <div className="contact-row">
                <span className="label-text">GSTIN</span>
                <span className="value-text">
                  {cart.gst_number || "-"}
                  {cart.gst_number ? (
                    <button
                      className="copy-btn"
                      type="button"
                      aria-label="Copy GSTIN"
                      onClick={() => handleCopyField(cart.gst_number, "GSTIN")}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  ) : null}
                </span>
              </div>
            </div>
            <div className="customer-footer-row">
              <span><strong>{cart.previous_order_count || 0}</strong> previous orders</span>
              <span
                className="profile-link"
                role="button"
                tabIndex={0}
                onClick={() => window.open(`/admin/customer/customerDetails/${cart.customer_mobile}`, "_blank")}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    window.open(`/admin/customer/customerDetails/${cart.customer_mobile}`, "_blank");
                  }
                }}
              >
                View user profile →
              </span>
            </div>
          </div>

          {!isClosedStatus && (
            <>
              <div className="side-card">
                <div className="side-card-title">ASSIGNED TO</div>
                <div className="assign-wrap" onClick={(e) => e.stopPropagation()}>
                  <Popover
                    trigger="click"
                    placement="bottomLeft"
                    open={assignOpen}
                    onOpenChange={(open) => {
                      setAssignOpen(open);
                      if (!open) setAssignSearch("");
                    }}
                    overlayClassName="assign-popover-overlay"
                    content={
                      <div className="assign-popover-card" onClick={(e) => e.stopPropagation()}>
                        <AntInput
                          className="assign-search-input"
                          placeholder="Search teammates..."
                          value={assignSearch}
                          onChange={(e) => setAssignSearch(e.target.value)}
                        />
                        <div className="assign-options-list">
                          {assigneeOptions
                            .filter((opt) => {
                              const q = assignSearch.trim().toLowerCase();
                              if (!q) return true;
                              return (
                                opt.label.toLowerCase().includes(q) ||
                                opt.searchText.includes(q)
                              );
                            })
                            .map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className="assign-option-row"
                                onClick={() => handleAssignUser(opt)}
                              >
                                <span className={`assign-opt-avatar ${opt.chip}`}>{opt.initials}</span>
                                <span className="assign-opt-name">{opt.label}</span>
                              </button>
                            ))}
                        </div>
                      </div>
                    }
                  >
                    {assignedTo ? (
                      <span className={`assign-chip ${(assigneeOptions.find((a) => a.value === assignedTo) || {}).chip || "chip-blue"}`}>
                        <span className="avatar">{(assigneeOptions.find((a) => a.value === assignedTo) || {}).initials || "PR"}</span>
                        <span className="name">{assignedTo}</span>
                        <button
                          type="button"
                          className="remove"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setAssignedTo(undefined);
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ) : (
                      <button type="button" className="assign-empty-btn-inline">+ Assign</button>
                    )}
                  </Popover>
                </div>
              </div>

              <div className="side-card">
                <div className="side-card-title">CUSTOMER TYPE</div>
                <div className="assign-wrap" onClick={(e) => e.stopPropagation()}>
                  <Popover
                    trigger="click"
                    placement="bottomLeft"
                    open={customerTypeOpen}
                    onOpenChange={(open) => {
                      setCustomerTypeOpen(open);
                      if (!open) setCustomerTypeSearch("");
                    }}
                    overlayClassName="assign-popover-overlay"
                    content={
                      <div className="assign-popover-card" onClick={(e) => e.stopPropagation()}>
                        <AntInput
                          className="assign-search-input"
                          placeholder="Search customer type..."
                          value={customerTypeSearch}
                          onChange={(e) => setCustomerTypeSearch(e.target.value)}
                        />
                        <div className="assign-options-list">
                          {customerTypeOptions
                            .filter((opt) => {
                              const q = customerTypeSearch.trim().toLowerCase();
                              if (!q) return true;
                              return (
                                opt.label.toLowerCase().includes(q) ||
                                opt.searchText.includes(q)
                              );
                            })
                            .map((opt) => (
                              <button
                                key={opt.value}
                                type="button"
                                className="assign-option-row"
                                onClick={() => handleCustomerTypeChange(opt.value)}
                                disabled={updateStatusLoading}
                              >
                                <span className={`assign-opt-avatar ${opt.chip}`}>{opt.initials}</span>
                                <span className="assign-opt-name">{opt.label}</span>
                              </button>
                            ))}
                        </div>
                      </div>
                    }
                  >
                    {customerType ? (
                      <span className={`assign-chip ${(customerTypeOptions.find((a) => a.value === customerType) || {}).chip || "chip-blue"}`}>
                        <span className="avatar">{(customerTypeOptions.find((a) => a.value === customerType) || {}).initials || "CT"}</span>
                        <span className="name">{customerType}</span>
                        <button
                          type="button"
                          className="remove"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setCustomerType(undefined);
                          }}
                        >
                          ×
                        </button>
                      </span>
                    ) : (
                      <button type="button" className="assign-empty-btn-inline">+ Select</button>
                    )}
                  </Popover>
                </div>
              </div>

              <div className="side-card">
                <div className="side-card-title">Actions</div>
                <Popconfirm
                  title="Mark as recovered?"
                  description="This cart status will be updated to recovered."
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => handleStatusUpdate("recovered")}
                >
                  <button
                    className="ac-action-btn filled-success"
                    type="button"
                    disabled={updateStatusLoading}
                  >
                    <span className="icon-bg">✓</span>
                    <span className="text-block">
                      <div className="ac-label">Mark as Recovered</div>
                      <div className="ac-desc">Customer completed checkout</div>
                    </span>
                  </button>
                </Popconfirm>
                <Popconfirm
                  title="Close this cart as lost?"
                  description="This cart status will be updated to lost."
                  okText="Yes"
                  cancelText="No"
                  onConfirm={() => handleStatusUpdate("lost")}
                >
                  <button
                    className="ac-action-btn filled-danger"
                    type="button"
                    disabled={updateStatusLoading}
                  >
                    <span className="icon-bg">×</span>
                    <span className="text-block">
                      <div className="ac-label">Close as Lost</div>
                      <div className="ac-desc">Pick reason &amp; close lead</div>
                    </span>
                  </button>
                </Popconfirm>

                <div className="action-divider"><span>OR SEND A NUDGE</span></div>

                <button
                  className="ac-action-btn-outline whatsapp"
                  type="button"
                  disabled
                >
                  <span className="icon-bg">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                      <path d="M17.498 14.382c-.301-.15-1.767-.867-2.04-.966-.273-.101-.473-.15-.673.15-.197.295-.771.964-.944 1.162-.175.195-.349.21-.646.075-.3-.15-1.263-.465-2.403-1.485-.888-.795-1.484-1.77-1.66-2.07-.174-.3-.019-.465.13-.615.136-.135.301-.345.451-.523.146-.181.194-.301.297-.496.1-.21.049-.375-.025-.524-.075-.15-.672-1.62-.922-2.206-.24-.584-.487-.51-.672-.51-.172-.015-.371-.015-.571-.015-.2 0-.523.074-.797.359-.273.3-1.045 1.02-1.045 2.475s1.07 2.865 1.219 3.075c.149.195 2.105 3.195 5.1 4.485.714.3 1.27.48 1.704.629.714.227 1.365.195 1.88.121.574-.091 1.767-.721 2.016-1.426.255-.705.255-1.29.18-1.425-.074-.135-.27-.21-.57-.345m-5.446 7.443h-.016c-1.77 0-3.524-.48-5.055-1.38l-.36-.214-3.75.975 1.005-3.645-.239-.375a9.869 9.869 0 0 1-1.516-5.26c0-5.445 4.455-9.885 9.942-9.885 2.654 0 5.145 1.035 7.021 2.91 1.875 1.859 2.909 4.35 2.909 6.99-.004 5.444-4.46 9.885-9.935 9.885M20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.893c0 2.096.549 4.14 1.595 5.945L0 24l6.335-1.652a12.062 12.062 0 0 0 5.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.896 0-3.176-1.24-6.165-3.495-8.411"></path>
                    </svg>
                  </span>
                  <span className="text-block">
                    <div className="ac-label">Send WhatsApp now</div>
                    <div className="ac-desc">Coming soon</div>
                  </span>
                </button>
                <button
                  className="ac-action-btn-outline email"
                  type="button"
                  disabled
                >
                  <span className="icon-bg">✉</span>
                  <span className="text-block">
                    <div className="ac-label">Send email reminder</div>
                    <div className="ac-desc">Coming soon</div>
                  </span>
                </button>
              </div>

              <div className="side-card">
                <div className="side-card-title">Add a note</div>
                <div className="notes-area">
                  <textarea
                    placeholder="Add follow-up note for this abandoned cart (customer intent, objections, next action, callback time)"
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                  />
                  <div className="notes-actions">
                    <button className="notes-save" onClick={handleSaveNote} disabled={!canSaveNote}>
                      Save note
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="side-card">
            <div className="side-card-head-row">
              <div className="side-card-title">Activity</div>
              <button
                type="button"
                className={`collapse-btn ${activityCollapsed ? "collapsed" : ""}`}
                onClick={() => setActivityCollapsed((prev) => !prev)}
              >
                {activityCollapsed ? "Expand" : "Collapse"}
                <svg className="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
                  <path d="m6 9 6 6 6-6"></path>
                </svg>
              </button>
            </div>
            {!activityCollapsed ? <div className="timeline">
              <div className="timeline-rail" />
              {activityItems.length ? (
                activityItems.map((item) => (
                  <div className="timeline-event" key={item.key}>
                    <div className={`timeline-icon ${item.iconType}`}>{renderTimelineIcon(item.iconType)}</div>
                    <div className="timeline-content">
                      <div className="timeline-title">{item.title}</div>
                      {item.message ? <div className="timeline-meta">{item.message}</div> : null}
                      {item.meta ? <div className="timeline-meta">{dateFormat(item.meta)}</div> : null}
                      {item.note ? <div className="timeline-note">{item.note}</div> : null}
                    </div>
                  </div>
                ))
              ) : (
                <div className="activity-collapsed-text">No activity yet</div>
              )}
            </div> : <div className="activity-collapsed-text"><strong>{activityItems.length}</strong> events</div>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AbandonedCartDetails;
